import { makeAutoObservable } from "mobx";
import type { MapRef } from "react-map-gl/mapbox";
import type { RefObject } from "react";
import type { GeoJSONSourceSpecification, LineLayerSpecification, MapMouseEvent } from "mapbox-gl";
import { MapBoxSource } from "@/components/Map/stores/MapBoxSource";
import { MapBoxLayer } from "@/components/Map/stores/MapBoxLayer";
import type { LngLatTuple } from "@/components/Map/stores/MarkersStore";

const PREVIEW_LINE_ID = "network-connections-preview-line";
const PREVIEW_SOURCE_ID = "network-connections-preview";
const BELOW_LAYER_ID = "markers-layer";
const THEME_COLOR = "#003BFF";

const emptyFC = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });

const previewSourceSpec: GeoJSONSourceSpecification = { type: "geojson", data: emptyFC() };

const previewLineSpec: LineLayerSpecification = {
	id: PREVIEW_LINE_ID,
	type: "line",
	source: PREVIEW_SOURCE_ID,
	paint: {
		"line-color": THEME_COLOR,
		"line-width": 2,
		"line-dasharray": [2, 2],
		"line-opacity": 0.6,
	},
};

export class ConnectionPreviewStore {
	selectedFromMarkerStructureId: Dryp.StructureId | null = null;
	loadingLine: { from: LngLatTuple; to: LngLatTuple } | null = null;
	private source = new MapBoxSource({ id: PREVIEW_SOURCE_ID, spec: previewSourceSpec });
	private line = new MapBoxLayer({ id: PREVIEW_LINE_ID, spec: previewLineSpec });
	private mouseMoveHandler?: (event: MapMouseEvent) => void;
	private keyDownHandler?: (event: KeyboardEvent) => void;

	constructor(
		private mapRef: RefObject<MapRef | null>,
		private getLngLat: (id: string) => LngLatTuple | null,
	) {
		makeAutoObservable(this, {
			source: false,
			line: false,
			mapRef: false,
			getLngLat: false,
		} as any);
	}

	selectFromMarker = (structureId: Dryp.StructureId | null) => {
		this.selectedFromMarkerStructureId = structureId;
		if (structureId) {
			this.drawLine();
		} else {
			this.removeDrawnLine();
		}
	};

	showLoadingLine = (from: LngLatTuple, to: LngLatTuple) => {
		const map = this.mapRef.current?.getMap();
		if (!map) {
			return;
		}
		if (this.mouseMoveHandler) {
			map.off("mousemove", this.mouseMoveHandler);
			this.mouseMoveHandler = undefined;
		}
		// Past the aiming phase: stop listening for Escape so it can't desync the
		// loading UI (the in-flight create cannot be cancelled from here).
		if (this.keyDownHandler) {
			document.removeEventListener("keydown", this.keyDownHandler);
			this.keyDownHandler = undefined;
		}
		this.source.attach(map);
		this.line.attach(map, { beforeId: BELOW_LAYER_ID });
		this.source.setGeoJSONData({
			type: "FeatureCollection",
			features: [
				{
					type: "Feature",
					properties: {},
					geometry: { type: "LineString", coordinates: [from, to] },
				},
			],
		});
		this.loadingLine = { from, to };
	};

	private drawLine() {
		this.removeDrawnLine();
		const map = this.mapRef.current?.getMap();
		if (!map) {
			return;
		}
		this.source.attach(map);
		this.line.attach(map, { beforeId: BELOW_LAYER_ID });
		this.mouseMoveHandler = event => {
			const fromId = this.selectedFromMarkerStructureId;
			const from = fromId ? this.getLngLat(String(fromId)) : null;
			if (!from) {
				return;
			}
			this.source.setGeoJSONData({
				type: "FeatureCollection",
				features: [
					{
						type: "Feature",
						properties: {},
						geometry: {
							type: "LineString",
							coordinates: [from, [event.lngLat.lng, event.lngLat.lat]],
						},
					},
				],
			});
		};
		map.on("mousemove", this.mouseMoveHandler);

		this.keyDownHandler = event => {
			if (event.key === "Escape") {
				this.selectFromMarker(null);
			}
		};
		document.addEventListener("keydown", this.keyDownHandler);
	}

	private removeDrawnLine() {
		const map = this.mapRef.current?.getMap();
		if (map && this.mouseMoveHandler) {
			map.off("mousemove", this.mouseMoveHandler);
			this.mouseMoveHandler = undefined;
		}
		if (this.keyDownHandler) {
			document.removeEventListener("keydown", this.keyDownHandler);
			this.keyDownHandler = undefined;
		}
		this.source.setGeoJSONData(emptyFC());
		this.loadingLine = null;
	}
}
